import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import { Collapse } from '../../components/Collapse';
import { Portal } from '../../components/Portal';
import { SelectSize } from '../../theme/Form';
import { Icon } from '../Icon';
import { Text } from '../Text';
import {
  StyledDropdownContainer,
  StyledDropdownOptionWrapper,
  StyledDropdownOptionsContainer,
  StyledDropdownWrapper,
} from './Dropdown.style';

export interface DropdownOptionProps {
  value: string;
  label: string;
  isSelected: boolean;
  icon?: string;
}

export interface DropdownDimensions {
  top: number;
  left: number;
  width: number;
}

export interface DropdownProps {
  children?: React.ReactNode;
  options?: Array<DropdownOptionProps>;
  dimensions: DropdownDimensions;
  opened?: boolean;
  height?: keyof typeof SelectSize;
  onSelect?: (selectedValue: string, selectedIndex: number) => void;
}

export function Dropdown({
  dimensions,
  children,
  options,
  height = 'Small',
  opened = false,
  onSelect,
}: DropdownProps) {
  const theme = useTheme();
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const handleSelect = (selectedValue: string, selectedIndex: number) => {
    setIsOpened(false);
    onSelect?.(selectedValue, selectedIndex);
  };

  useEffect(() => {
    setIsOpened(opened);
  }, [opened]);

  return (
    <Portal>
      <StyledDropdownContainer
        className="dropdown-container"
        style={{
          top: dimensions.top,
          left: dimensions.left,
          width: dimensions.width,
        }}
      >
        <Collapse isOpened={isOpened}>
          <StyledDropdownWrapper>
            <StyledDropdownOptionsContainer $size={height}>
              {options
                ? options.map((option, index) => {
                    return (
                      <StyledDropdownOptionWrapper
                        key={`dropdown-option-${option.label}-${index}`}
                        onClick={() => handleSelect(option.value, index)}
                      >
                        {option.icon && (
                          <Icon name={option.icon} color={theme.Color.Neutrals.Dark} size="Small" />
                        )}
                        <Text size={'Body'}>{option.label}</Text>
                      </StyledDropdownOptionWrapper>
                    );
                  })
                : children}
            </StyledDropdownOptionsContainer>
          </StyledDropdownWrapper>
        </Collapse>
      </StyledDropdownContainer>
    </Portal>
  );
}
